// src/App.tsx
import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import LayoutShell from "./components/LayoutShell";

import Login from "./pages/Login";
import RequireAuth from "./auth/RequireAuth";
import RequireRole from "./auth/RequireRole";
import RequireSuperAdmin from "./auth/RequireSuperAdmin";
import { ADMIN_ROLES, CLIENT_VISIBLE_ROLES, OPERATIONAL_ROLES } from "./auth/roles";
import { getPermissionDeniedMessage, isPermissionDeniedError } from "./lib/firebaseErrors";

const Home = lazy(() => import("./pages/Home"));
const StockOverview = lazy(() => import("./pages/StockOverview"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const CreateProduct = lazy(() => import("./pages/CreateProduct"));
const DuplicateProduct = lazy(() => import("./pages/DuplicateProduct"));
const StockCheck = lazy(() => import("./pages/StockCheck"));
const StockTransfer = lazy(() => import("./pages/StockTransfer"));
const StockChange = lazy(() => import("./pages/StockChange"));
const BatchStockTransfer = lazy(() => import("./pages/BatchStockTransfer"));
const Build = lazy(() => import("./pages/Build"));
const BuildHistory = lazy(() => import("./pages/BuildHistory"));
const SalesOverview = lazy(() => import("./pages/SalesOverview"));
const OldestUnpickedOrders = lazy(() => import("./pages/OldestUnpickedOrders"));
const OrderPickDetail = lazy(() => import("./pages/OrderPickDetail"));
const ImportPick = lazy(() => import("./pages/ImportPick"));
const TransactionsList = lazy(() => import("./pages/TransactionsList"));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders"));
const PurchaseCreate = lazy(() => import("./pages/PurchaseCreate"));
const PurchaseImportHcp = lazy(() => import("./pages/PurchaseImportHcp"));
const PurchaseReceive = lazy(() => import("./pages/PurchaseReceive"));
const Receiving = lazy(() => import("./pages/Receiving"));
const PurchaseReceipts = lazy(() => import("./pages/PurchaseReceipts"));
const BatchPick = lazy(() => import("./pages/BatchPick"));
const BatchPickRun = lazy(() => import("./pages/BatchPickRun"));
const LookupsAdmin = lazy(() => import("./pages/LookupsAdmin"));
const MarketplaceConnections = lazy(() => import("./pages/MarketplaceConnections"));
const SublocationAdmin = lazy(() => import("./pages/admin/SublocationAdmin"));
const RestockReport = lazy(() => import("./pages/RestockReport"));
const StockReports = lazy(() => import("./pages/StockReports"));
const PrintLabel = lazy(() => import("./pages/PrintLabel"));
const CycleCount = lazy(() => import("./pages/reports/CycleCount"));
const CycleCountReview = lazy(() => import("./pages/reports/CycleCountReview"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));

class RouteChunkBoundary extends React.Component<
  React.PropsWithChildren,
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error("Failed to load application route", error);
  }

  render() {
    if (this.state.failed) {
      return (
        <div style={{ padding: 32, fontSize: 14 }} role="alert">
          <p>This page could not be loaded. The application may have been updated.</p>
          <button type="button" onClick={() => window.location.reload()}>
            Reload application
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RouteLoading() {
  return <div style={{ padding: 32, fontSize: 14 }}>Loading page...</div>;
}

function OrdersToPickingRedirect() {
  const { id } = useParams();
  return <Navigate to={`/picking/${id}`} replace />;
}

export default function App() {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isPermissionDeniedError(event.reason)) return;
      event.preventDefault();
      alert(getPermissionDeniedMessage());
      console.warn("Blocked Firebase action:", event.reason);
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  }, []);

  const operational = (element: React.ReactElement) => (
    <RequireRole allowedRoles={OPERATIONAL_ROLES}>{element}</RequireRole>
  );
  const clientVisible = (element: React.ReactElement) => (
    <RequireRole allowedRoles={CLIENT_VISIBLE_ROLES}>{element}</RequireRole>
  );

  return (
    <BrowserRouter>
      <RouteChunkBoundary>
        <Suspense fallback={<RouteLoading />}>
          <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/print/label" element={<RequireAuth><PrintLabel /></RequireAuth>} />

        <Route element={<RequireAuth><LayoutShell /></RequireAuth>}>
          <Route index element={clientVisible(<Home />)} />

          {/* Inventory */}
          <Route path="stock" element={clientVisible(<StockOverview />)} />
          <Route path="product/new" element={<RequireSuperAdmin><CreateProduct /></RequireSuperAdmin>} />
          <Route
            path="product/:id/duplicate"
            element={<RequireRole allowedRoles={ADMIN_ROLES}><DuplicateProduct /></RequireRole>}
          />
          <Route path="product/:id" element={clientVisible(<ProductDetail />)} />
          <Route path="stock-check" element={<Navigate to="/stock-check/item" replace />} />
          <Route path="stock-check/item" element={operational(<StockCheck mode="item" />)} />
          <Route path="stock-check/sublocation" element={operational(<StockCheck mode="location" />)} />
          <Route path="transfer" element={operational(<StockTransfer />)} />
          <Route path="change" element={operational(<StockChange />)} />
          <Route
            path="batch-transfer"
            element={<RequireRole allowedRoles={ADMIN_ROLES}><BatchStockTransfer /></RequireRole>}
          />
          <Route path="build" element={operational(<Build />)} />
          <Route path="build/history" element={operational(<BuildHistory />)} />
          <Route path="transactions" element={operational(<TransactionsList />)} />

          {/* Sales */}
          <Route path="orders" element={clientVisible(<SalesOverview />)} />
          <Route path="orders/oldest-unpicked" element={operational(<OldestUnpickedOrders />)} />
          <Route path="picking/:id" element={operational(<OrderPickDetail />)} />
          <Route path="orders/:id" element={operational(<OrdersToPickingRedirect />)} />
          <Route path="picks/import" element={operational(<ImportPick />)} />

          {/* Purchasing */}
          <Route path="purchase" element={operational(<PurchaseOrders />)} />
          <Route path="purchase/new" element={operational(<PurchaseCreate />)} />
          <Route path="purchase/import-hcp" element={operational(<PurchaseImportHcp />)} />
          <Route path="purchase/receipts" element={operational(<PurchaseReceipts />)} />
          <Route path="purchase/:poId/receive" element={operational(<PurchaseReceive />)} />
          <Route path="receiving" element={operational(<Receiving />)} />

          {/* Batch picking */}
          <Route path="picks/batch" element={operational(<BatchPick />)} />
          <Route path="picks/run" element={operational(<BatchPickRun />)} />

          {/* Reports */}
          <Route path="reports/restock" element={operational(<RestockReport />)} />
          <Route path="reports/stock" element={operational(<StockReports />)} />
          <Route path="reports/cycle-count" element={operational(<CycleCount />)} />
          <Route
            path="reports/cycle-count/review"
            element={<RequireRole allowedRoles={ADMIN_ROLES}><CycleCountReview /></RequireRole>}
          />

          {/* Admin */}
          <Route path="lookups" element={operational(<LookupsAdmin />)} />
          <Route
            path="admin/marketplaces"
            element={<RequireRole allowedRoles={ADMIN_ROLES}><MarketplaceConnections /></RequireRole>}
          />
          <Route
            path="admin/users"
            element={<RequireRole allowedRoles={ADMIN_ROLES}><UserManagement /></RequireRole>}
          />
          <Route
            path="admin/sublocations"
            element={<RequireSuperAdmin><SublocationAdmin /></RequireSuperAdmin>}
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
          </Routes>
        </Suspense>
      </RouteChunkBoundary>
    </BrowserRouter>
  );
}
